// V-06 fix: backend keyphrase endpoint is NOT paginated — returns all
// keyphrases in one response. Previous ?page=N was silently ignored and
// Previous/Next buttons were no-ops. Slicing is now client-side in
// batches of KEYPHRASE_PAGE_SIZE.

import React from "react";
import { Typography, Button, Select, Option } from "@material-tailwind/react";
import InfoDialog from "components/dialog/info-dialog";
import Pagination from "components/pagination";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";
import {
    sentimentByKeyPhraseFiltered,
    sentimentRangeFormat,
} from "../data-transforms";

// Client-side page size for the keyphrase table. The backend returns
// all keyphrases in one shot (no server-side pagination), so we slice
// the filtered array into batches of this size. 20 matches the page
// size used elsewhere in the reports tab for question-sentiment pages.
const KEYPHRASE_PAGE_SIZE = 20;

export function KeyphrasesSection({
    sentimentByKeyphrase,
    keyphraseSentiment,
    setKeyphraseSentiment,
    keyphrasesPage,
    setKeyphrasesPage,
    setResponseSentimentIdList,
    setShowResponsesSentimentListDialog,
}) {
    const filteredKeyphrases = sentimentByKeyPhraseFiltered(
        sentimentByKeyphrase,
        keyphraseSentiment,
    );
    const totalFiltered = filteredKeyphrases?.length ?? 0;
    const lastPage = Math.max(1, Math.ceil(totalFiltered / KEYPHRASE_PAGE_SIZE));
    const page = Math.min(keyphrasesPage, lastPage);
    const pagedKeyphrases = (filteredKeyphrases ?? []).slice(
        (page - 1) * KEYPHRASE_PAGE_SIZE,
        page * KEYPHRASE_PAGE_SIZE,
    );
    return (
        <div>
            <div className="flex justify-between flex-wrap">
                <Typography
                    variant="h4"
                    className="ml-2 text-black w-2/3"
                >
                    Keyphrases and Keyphrase Sentiment
                </Typography>
                <InfoDialog title="Keyphrase Sentiment Table Guide">
                    <Typography
                        as="span"
                        className="font-body text-primary"
                    >
                        <div className="space-y-4 text-black text-lg">
                            <Typography className="font-body">
                                The table below shows a sentiment score
                                for the keyphrases deduced from the
                                survey respondents answers. Where we
                                show sentiment scores it is taken from a
                                division into how the key phrase is
                                divided by the following sentiments:
                                positive, negative, mixed, neutral. It
                                is important to note that these are not
                                scores of a good or bad, just the
                                language sentiment. It is split into the
                                following columns:
                            </Typography>

                            <ul className="list-disc list-inside font-body text-gray-900 space-y-1">
                                <li>
                                    Keyphrase: The keyphrase that Amazon
                                    Comprehend has identified in the
                                    text
                                </li>
                                <li>
                                    Sentiment: This shows the highest
                                    scoring sentiment type(s)
                                </li>
                                <li>
                                    Key Sentiment Range: This shows the
                                    range of sentiment strength for the
                                    key sentiment by percentage
                                </li>
                                <li>
                                    Responses: Press this button to see
                                    all the responses that share this
                                    keyphrase with their individual
                                    scores
                                </li>
                                <li>
                                    Make Theme: Turn this keyphrase into
                                    a theme for theming your survey
                                </li>
                            </ul>

                            <Typography className="font-body">
                                The table can be viewed for all
                                Keyphrase response sentiments (combined
                                or &ldquo;All&rdquo;) or by sentiment type
                                (positive, negative, mixed, neutral) by
                                using the drop down at the top of the
                                table.
                            </Typography>

                            <Typography className="mt-2 font-body text-black">
                                Keyphrase analysis provided by Amazon
                                Comprehend LLM.
                                <br />
                                If there are multiple sentiments to a
                                keyword, it will show the range from min
                                to max.
                            </Typography>
                        </div>
                    </Typography>
                </InfoDialog>
            </div>
            <div className="bg-white mt-2 rounded-lg">
                {!!sentimentByKeyphrase && (
                    <>
                        <div className="w-[50%] mb-4">
                            <Select
                                label="Select a sentiment type"
                                value={keyphraseSentiment}
                                onChange={(value) => {
                                    setKeyphraseSentiment(value);
                                    // Reset to first page on filter change:
                                    // the filtered list length changes,
                                    // so the current page may now be out
                                    // of range. Avoids an empty-table UX.
                                    setKeyphrasesPage(1);
                                }}
                            >
                                <Option value="">All</Option>
                                <Option value="positive">
                                    Positive
                                </Option>
                                <Option value="neutral">
                                    Neutral
                                </Option>
                                <Option value="negative">
                                    Negative
                                </Option>
                                <Option value="mixed">Mixed</Option>
                            </Select>
                        </div>
                        <GobbyTable>
                            <GobbyTableHead className="text-sm sm:text-lg">
                                <GobbyTableRow className="pt-2">
                                    <GobbyTableHeader className="font-body w-[40%]">
                                        Keyphrase
                                    </GobbyTableHeader>
                                    <GobbyTableHeader className="font-body text-center w-[20%]">
                                        Sentiment
                                    </GobbyTableHeader>
                                    <GobbyTableHeader className="font-body text-center w-[20%]">
                                        Range
                                    </GobbyTableHeader>
                                    <GobbyTableHeader className="text-center font-body w-[20%]">
                                        Responses
                                    </GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody className="text-black text-right">
                                {pagedKeyphrases.map((keyphrase, index) => (
                                    <GobbyTableRow
                                        key={index}
                                    >
                                        <GobbyTableCell className="font-body text-left pl-2">
                                            {keyphrase.text}
                                        </GobbyTableCell>
                                        <GobbyTableCell className="text-center font-body capitalize">
                                            {
                                                keyphrase.overall_sentiment
                                            }
                                        </GobbyTableCell>
                                        <GobbyTableCell className="text-center font-body">
                                            {sentimentRangeFormat(
                                                keyphrase.responses,
                                                "keyphrase_score",
                                            )}
                                        </GobbyTableCell>
                                        <GobbyTableCell className="text-center">
                                            <Button
                                                type="button"
                                                variant="filled"
                                                className="btn btn--secondary m-1"
                                                onClick={() => {
                                                    const responseIdList =
                                                        keyphrase.responses?.map(
                                                            (r) =>
                                                                r?.respondent_answer_id,
                                                        );
                                                    setResponseSentimentIdList(
                                                        responseIdList,
                                                    );
                                                    setShowResponsesSentimentListDialog(
                                                        true,
                                                    );
                                                }}
                                            >
                                                View
                                            </Button>
                                        </GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                        <Pagination
                            currentPage={page}
                            lastPage={lastPage}
                            onPageChange={setKeyphrasesPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
